import type { ReactNode } from 'react'
import { ExternalLink } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { OrderHeader, PurchaseRequestSummary, QuoteHeader, Shipment } from '@/lib/api-types'
import { formatDate, formatMoney } from '@/lib/format'
import {
  PURCHASE_REQUEST_STATUS_LABELS,
  purchaseRequestChipClasses,
  quoteIsExpired,
  stageChipClasses,
} from '@/lib/status'
import { trackingUrl } from '@/lib/tracking'
import { orderNumber } from '@/features/orders/order-number'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { StatTile } from '@/components/ui/stat-tile'
import { StatusChip } from '@/components/ui/status-chip'

const DAY_MS = 24 * 60 * 60 * 1000

/** Not yet expired, but the validity window closes within 30 days. */
function expiringSoon(quote: QuoteHeader): boolean {
  if (!quote.expirationDate || quoteIsExpired(quote.expirationDate, quote.status)) return false
  const expires = new Date(quote.expirationDate).getTime()
  return !Number.isNaN(expires) && expires - Date.now() <= 30 * DAY_MS
}

/** Tile value slot: skeleton while loading, em-dash on failure. */
function tileValue(pending: boolean, failed: boolean, value: ReactNode): ReactNode {
  if (pending) return <Skeleton className="h-7 w-12" />
  if (failed) return '—'
  return value
}

const RECENT_ORDER_COLUMNS: DataTableColumn<OrderHeader>[] = [
  {
    id: 'number',
    header: 'Order #',
    widthClassName: 'w-32',
    nowrap: true,
    cell: (o) => (
      <Link
        to={`/orders/${o.id}`}
        onClick={(event) => event.stopPropagation()}
        className="rounded-sm font-mono text-[0.8125rem] text-inkMid no-underline underline-offset-4 hover:text-brand hover:underline"
      >
        {orderNumber(o)}
      </Link>
    ),
  },
  {
    id: 'po',
    header: 'PO number',
    flex: true,
    widthClassName: 'min-w-32',
    cell: (o) => (
      <span className="text-sm text-inkMid" title={o.poNumber ?? undefined}>
        {o.poNumber ?? '—'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    nowrap: true,
    cell: (o) => <StatusChip label={o.stageLabel} chipClasses={stageChipClasses(o.stage)} />,
  },
  {
    id: 'amount',
    header: 'Amount',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (o) => formatMoney(o.amount, o.currency),
  },
]

/**
 * The signed-in landing page: what's moving on the account right now — open
 * orders, the balance, order requests in review, quotes about to close — with the
 * newest activity underneath. Tiles read from the same queries the section
 * pages use, so landing here warms their caches.
 */
export function HomePage() {
  const navigate = useNavigate()

  const ordersQuery = useQuery({
    queryKey: ['orders', 'open'],
    queryFn: ({ signal }) => api.listOrders('open', { signal }),
    staleTime: 15_000,
  })
  const invoicesQuery = useQuery({
    queryKey: ['invoices', 'open'],
    queryFn: ({ signal }) => api.listInvoices('open', { signal }),
    staleTime: 15_000,
  })
  const requestsQuery = useQuery({
    queryKey: ['purchase-requests'],
    queryFn: ({ signal }) => api.listPurchaseRequests({ signal }),
    staleTime: 15_000,
  })
  const quotesQuery = useQuery({
    queryKey: ['quotes'],
    queryFn: ({ signal }) => api.listQuotes({ signal }),
    staleTime: 15_000,
  })

  const openOrders = ordersQuery.data?.orders ?? []
  const overdueCount = (invoicesQuery.data?.invoices ?? []).filter(
    (inv) => inv.paymentGroup === 'overdue',
  ).length
  const pendingRequests = (requestsQuery.data?.requests ?? []).filter(
    (r) => r.status === 'submitted',
  )
  const quotesExpiring = (quotesQuery.data?.quotes ?? []).filter(expiringSoon).length

  // Recent shipments hydrate from the 3 newest open orders' detail queries
  // (bounded fan-out; no dedicated endpoint). Failed or still-loading details
  // simply contribute nothing — the section only renders once there's cargo.
  const detailQueries = useQueries({
    queries: openOrders.slice(0, 3).map((o) => ({
      queryKey: ['order', o.id],
      queryFn: ({ signal }: { signal: AbortSignal }) => api.getOrder(o.id, { signal }),
      staleTime: 15_000,
    })),
  })
  const recentShipments = detailQueries
    .flatMap((q) => q.data?.shipments ?? [])
    .sort((a, b) => (b.shippedAt ?? '').localeCompare(a.shippedAt ?? ''))
    .slice(0, 4)

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title="Dashboard"
        description="Your account with Green Line at a glance."
      />

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Open orders"
          to="/orders"
          value={tileValue(
            ordersQuery.isPending,
            ordersQuery.isError,
            ordersQuery.data?.counts.open,
          )}
        />
        <StatTile
          label="Open balance"
          to="/invoices"
          emphasis={overdueCount > 0}
          value={tileValue(
            invoicesQuery.isPending,
            invoicesQuery.isError,
            formatMoney(
              invoicesQuery.data?.openBalance,
              invoicesQuery.data?.invoices[0]?.currency ?? 'USD',
            ),
          )}
          hint={
            overdueCount > 0
              ? `${overdueCount} overdue`
              : undefined
          }
        />
        <StatTile
          label="Pending order requests"
          to="/purchasing"
          value={tileValue(requestsQuery.isPending, requestsQuery.isError, pendingRequests.length)}
          hint="Waiting on Green Line review"
        />
        <StatTile
          label="Quotes expiring"
          to="/quotes"
          value={tileValue(quotesQuery.isPending, quotesQuery.isError, quotesExpiring)}
          hint="Within 30 days"
        />
      </div>

      <section aria-labelledby="home-recent-orders-heading" className="mt-10">
        <SectionHeader
          id="home-recent-orders-heading"
          title="Recent orders"
          action={
            <Button asChild variant="quiet" size="sm">
              <Link to="/orders">View all orders</Link>
            </Button>
          }
        />
        <div className="mt-4">
          {ordersQuery.isPending ? (
            <SkeletonText lines={4} />
          ) : ordersQuery.isError ? (
            <ErrorState
              message="We couldn’t load your orders."
              onRetry={() => void ordersQuery.refetch()}
            />
          ) : (
            <DataTable<OrderHeader>
              columns={RECENT_ORDER_COLUMNS}
              rows={openOrders.slice(0, 5)}
              rowKey={(o) => o.id}
              onRowClick={(o) => navigate(`/orders/${o.id}`)}
              caption="Your most recent open orders"
              empty={<p className="py-6 text-sm text-mute">No open orders right now.</p>}
              renderCard={(o) => <RecentOrderCard order={o} />}
            />
          )}
        </div>
      </section>

      <div className="mt-10 grid items-start gap-x-8 gap-y-10 lg:grid-cols-2">
        <section aria-labelledby="home-pending-requests-heading">
          <SectionHeader id="home-pending-requests-heading" title="Pending order requests" />
          <div className="mt-4">
            {requestsQuery.isPending ? (
              <SkeletonText lines={3} />
            ) : requestsQuery.isError ? (
              <ErrorState
                message="We couldn’t load your order requests."
                onRetry={() => void requestsQuery.refetch()}
              />
            ) : pendingRequests.length === 0 ? (
              <p className="text-sm text-mute">
                Nothing in review right now.{' '}
                <Link
                  to="/purchasing/new"
                  className="rounded-sm font-semibold text-brand underline-offset-4 hover:underline"
                >
                  Start a purchase order request
                </Link>
                .
              </p>
            ) : (
              <ul className="space-y-2">
                {pendingRequests.slice(0, 3).map((request) => (
                  <li key={request.id}>
                    <PendingRequestCard request={request} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {recentShipments.length > 0 ? (
          <section aria-labelledby="home-shipments-heading">
            <SectionHeader id="home-shipments-heading" title="Recent shipments" />
            <ul className="mt-4 space-y-2">
              {recentShipments.map((shipment) => (
                <li key={shipment.id}>
                  <ShipmentRow shipment={shipment} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  )
}

/** Narrow-screen fallback for the recent-orders table. */
function RecentOrderCard({ order }: { order: OrderHeader }) {
  return (
    <Link to={`/orders/${order.id}`} className="block no-underline">
      <Card
        padding="sm"
        className="transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream"
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[0.8125rem] text-inkMid">{orderNumber(order)}</span>
          <span className="font-mono text-[0.8125rem] text-inkMid">
            {formatMoney(order.amount, order.currency)}
          </span>
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-2">
          <StatusChip label={order.stageLabel} chipClasses={stageChipClasses(order.stage)} />
          {order.poNumber ? <span className="text-xs text-mute">PO {order.poNumber}</span> : null}
        </span>
      </Card>
    </Link>
  )
}

function PendingRequestCard({ request }: { request: PurchaseRequestSummary }) {
  return (
    <Link to={`/purchasing/${request.id}`} className="block no-underline">
      <Card
        padding="sm"
        className="transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream"
      >
        <span className="flex flex-wrap items-baseline justify-between gap-3">
          <span className="font-mono text-[0.8125rem] text-inkMid">{request.name}</span>
          <span className="font-mono text-[0.8125rem] text-inkMid">
            {formatMoney(request.amount, request.currency)}
          </span>
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-mute">
          <StatusChip
            label={PURCHASE_REQUEST_STATUS_LABELS[request.status]}
            chipClasses={purchaseRequestChipClasses(request.status)}
          />
          {request.poNumber ? <span>PO {request.poNumber}</span> : null}
          {request.submittedAt ? <span>Sent {formatDate(request.submittedAt)}</span> : null}
        </span>
      </Card>
    </Link>
  )
}

function ShipmentRow({ shipment }: { shipment: Shipment }) {
  const url = trackingUrl(shipment.carrier, shipment.trackingNumber)
  return (
    <Card padding="sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-[0.8125rem] text-inkMid">
          Delivery {shipment.docNum ?? '—'}
        </span>
        <span className="text-xs text-mute">
          {shipment.shippedAt ? `Shipped ${formatDate(shipment.shippedAt)}` : 'Ship date pending'}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-inkMid">
        {shipment.carrier ? <span>{shipment.carrier}</span> : null}
        {shipment.trackingNumber ? (
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-sm font-mono text-[0.8125rem] font-semibold text-brand underline-offset-4 hover:underline"
            >
              {shipment.trackingNumber}
              <ExternalLink aria-hidden="true" className="size-3.5" />
              <span className="sr-only">(opens carrier tracking)</span>
            </a>
          ) : (
            <span className="font-mono text-[0.8125rem]">{shipment.trackingNumber}</span>
          )
        ) : null}
      </div>
    </Card>
  )
}
